<script>
import { uuidv4 } from '@/api/uuid'
import { logger } from '@/logger'
import DialogMixin from '@/mixins/DialogMixin'
import { getFilePrintSpecs, mergeUpload } from '@/api/api'
import { EventBus, Events } from '@/eventbus/EventBus'
import { find } from 'lodash-es'
import { useFileListStore } from '@/stores/FileList.store'
import { mapStores } from 'pinia'
import { UPLOAD_COOKIE } from '@/stores/store'
import Cookies from 'js-cookie'

export default {
  name: 'OrderFileUploader',
  mixins: [DialogMixin],
  props: ['dropTarget', 'uploadFileType', 'accept'],
  emits: [
    'upload-done',
    'files-added',
    'file-progress',
    'file-error',
    'file-removed',
    'drag-over',
    'drag-leave'
  ],
  data() {
    return {
      uploaderOptions: {
        target: '/api/v3/upload',
        testChunks: false,
        generateUniqueIdentifier: () => {
          return uuidv4()
        },
        query: () => {
          return {
            sessionId: Cookies.get(UPLOAD_COOKIE),
            uploadFileType: this.uploadFileType
          }
        },
        maxChunkRetries: 3,
        chunkRetryInterval: 100,
        chunkSize: 524288, // 0.5MB
        permanentErrors: [404, 409, 415, 422, 500, 501]
      },
      duplicateFiles: [],
      uploader: undefined,
      dropElement: null,
      addedFileNames: [], // Track file names being added in current batch
      batchStarted: false, // Track if we're in a new batch
      filesAddedEmitted: false, // Track if we've already emitted files-added for this batch
      pendingMerges: 0 // Track how many mergeUpload calls are still pending
    }
  },
  created() {
    EventBus.on(Events.LOGGED_OFF, this.clearUploads)
  },
  mounted() {
    this.uploader = this.$refs.uploader.uploader
    this.assignDropTarget(this.dropTarget)
  },
  beforeUnmount() {
    this.clearDropTarget()
  },
  computed: {
    ...mapStores(useFileListStore)
  },
  watch: {
    dropTarget(newTarget) {
      this.assignDropTarget(newTarget)
    }
  },
  methods: {
    assignDropTarget(target) {
      if (!this.uploader) return

      this.clearDropTarget()
      if (!target) return

      const element = document.getElementById(target)
      if (!element) return

      this.uploader.assignDrop(element)
      this.dropElement = element
    },
    clearDropTarget() {
      if (this.uploader && this.dropElement) {
        this.uploader.unAssignDrop(this.dropElement)
        this.dropElement = null
      }
    },
    filesAdded(files) {
      // Clear the tracking arrays after batch is processed
      this.addedFileNames = []
      // Reset batch started flag for next batch
      this.batchStarted = false
      this.filesAddedEmitted = false

      if (files.length === 0) {
        this.$emit('upload-done')
      } else {
        this.$emit('files-added')
      }
    },
    duplicateFileWarning(filenames) {
      this.$emit('upload-done')
      this.$emit('drag-leave')

      // Remove duplicates from the array (in case same file was added multiple times)
      const uniqueFilenames = [...new Set(filenames)]

      const text =
        uniqueFilenames instanceof Array
          ? uniqueFilenames.length > 1
            ? `De volgende bestanden zijn reeds toegevoegd: <br><strong><br> ${uniqueFilenames.join(
                '<br>'
              )}</strong>`
            : `Het bestand <strong>${uniqueFilenames[0]}</strong> is reeds toegevoegd`
          : `Het bestand <strong>${uniqueFilenames}</strong> is reeds toegevoegd.`

      this.errorDialog('Waarschuwing', text)
    },
    fileRemoved(file) {
      // Clean up tracking arrays
      const nameIndex = this.addedFileNames.indexOf(file.name)
      if (nameIndex > -1) {
        this.addedFileNames.splice(nameIndex, 1)
      }
    },
    fileProgress(file) {
      const progress = Math.floor(this.uploader.progress() * 99)
      this.$emit('file-progress', progress)
    },
    fileError(rootFile, file, message) {
      this.inProgress = false
      this.uploadProgress = 0

      if (message.includes('duplicate')) {
        // Add to duplicates array instead of showing immediately
        // The warning will be shown after all files are processed
        if (!this.duplicateFiles.includes(file.name)) {
          this.duplicateFiles.push(file.name)
        }

        // Check if upload is done after error
        if (!this.$refs.uploader.uploader.isUploading()) {
          this.uploadDone()

          // Show duplicate warning if we have any
          if (this.duplicateFiles.length > 0) {
            this.duplicateFileWarning(this.duplicateFiles)
            this.duplicateFiles = []
          }
        }
      } else {
        this.errorDialog(
          'Probleem bij uploaden',
          `<p>Het uploaden van het bestand <strong>${file.name}</strong> is mislukt. Probeer het opnieuw.</p>
              <p>Foutmelding: ${message}</p>`
        )
      }

      this.$emit('file-error')
    },
    handleDragOver(event) {
      // Only accept actual files, not HTML elements from the page itself
      if (event?.dataTransfer?.types) {
        const hasFiles = event.dataTransfer.types.includes('Files')
        const hasHTML = event.dataTransfer.types.includes('text/html')

        // Reject if it's HTML content but not files
        if (hasHTML && !hasFiles) {
          event.dataTransfer.dropEffect = 'none'
          return
        }

        // Only emit drag-over if we have actual files
        if (hasFiles) {
          this.$emit('drag-over')
        }
      }
    },
    handleDragLeave() {
      this.$emit('drag-leave')
    },
    fileSuccess(rootFile, file, message, chunk) {
      const unzipOnUpload = this.rootStore.loggedIn
        ? this.rootStore.user.unzipOnUpload
        : true

      const analyseFile =
        this.rootStore.theme.customFields.analyseFiles || false

      try {
        const sessionId = Cookies.get(UPLOAD_COOKIE)
        mergeUpload(
          sessionId,
          file.uniqueIdentifier,
          file.name,
          file.relativePath,
          file.chunks.length,
          unzipOnUpload,
          analyseFile,
          this.uploadFileType
        )
          .then((result) => {
            // The result is the spec for only the uploaded file(s)
            this.fileListStore.updatePrintSpecs({ update: result })

            if (!this.$refs.uploader.uploader.isUploading()) {
              this.uploadDone()
            }
          })
          .catch((error) => {
            if (!this.$refs.uploader.uploader.isUploading()) {
              this.uploadDone()
            }

            this.notificationDialog(
              'Waarschuwing',
              `<p>Het uploaden van het bestand <strong>${file.name}</strong> is mislukt. Probeer het opnieuw.</p>
                <p>Foutmelding: ${error.status}</p>`,
              5000
            )
          })
      } catch (error) {
        logger.error(error)
        this.notificationDialog(
          'Waarschuwing',
          `<p>Het uploaden van het bestand <strong>${file.name}</strong> is mislukt. Probeer het opnieuw.</p>
              <p>Foutmelding: ${error.message}</p>`,
          5000
        )
      }
    },
    fileAdded(file) {
      // Clear duplicates array at the start of a new batch (first file only)
      if (!this.batchStarted) {
        this.batchStarted = true
        this.duplicateFiles = []
        this.filesAddedEmitted = false
      }

      if (!this.rootStore.loggedIn) {
        EventBus.once(Events.LOGGED_IN, () => this.checkAddedFile(file))
        EventBus.once(Events.LOGIN_CANCELLED, () => this.removeFile(file))
        this.$modal.show('login')
      } else {
        this.$nextTick(() => this.checkAddedFile(file))
      }
    },
    clearUploads() {
      if (this.$refs.uploader) {
        logger.debug('clearing uploads')
        this.$refs.uploader.uploader.cancel()
      }
    },
    removeFile(file) {
      this.$refs.uploader.uploader.removeFile(file)
      this.$emit('file-removed')
    },
    checkAddedFile(file) {
      // Filter out macOS system files
      const systemFiles = [
        '.DS_Store',
        '._.DS_Store',
        '__MACOSX',
        'Thumbs.db',
        'desktop.ini'
      ]
      const isSystemFile = systemFiles.some(
        (sysFile) =>
          file.name === sysFile ||
          file.name.startsWith('._') ||
          file.relativePath?.includes('__MACOSX')
      )

      if (isSystemFile) {
        file.ignored = true
        this.$refs.uploader.uploader.removeFile(file)
        return
      }

      // LOGGED_IN event might be fired after earlier LOGIN_CANCELLED - in that case the file will not be present anymore
      const filePresent = find(this.$refs.uploader.uploader.files, (object) => {
        return object.file.name === file.name
      })
      if (!filePresent) return

      // Check if file already exists in the store
      if (this.fileListStore.containsFile(file.name)) {
        this.duplicateFiles.push(file.name)
        file.ignored = true
        return
      }

      // Check if we've already added a file with this name in the current batch
      if (this.addedFileNames.includes(file.name)) {
        this.duplicateFiles.push(file.name)
        file.ignored = true
        return
      }

      // Add this filename to the tracking array
      this.addedFileNames.push(file.name)

      this.inProgress = true

      if (file.size > 2147483648) {
        this.errorDialog(
          file.name + ' kan niet worden geüpload',
          `Het bestand ${file.name} kan niet worden geüpload. De maximaal toegestane bestandsgrootte is 2GB.`
        )

        file.ignored = true
        return
      }

      this.$nextTick(() => {
        this.fileListStore.isUploading = true
        this.$refs.uploader.uploader.resume()

        // The vue-simple-uploader doesn't emit 'on-files-added' when auto-start is true
        // and we manually call resume(). So we need to manually emit 'files-added' here.
        // Emit immediately - only once per batch
        if (!this.filesAddedEmitted && this.addedFileNames.length > 0) {
          this.filesAddedEmitted = true
          this.$emit('files-added')
        }
      })
    },
    uploadDone() {
      // Reset flags for next upload
      this.batchStarted = false
      this.filesAddedEmitted = false
      this.addedFileNames = []
      this.pendingMerges = 0

      this.fileListStore.isUploading = false
      this.$emit('upload-done')
      this.pollAnalyzingState()
    },
    pollAnalyzingState() {
      // See if files are still being processed
      if (
        this.fileListStore.getAllFiles.filter(
          (file) =>
            file.status !== 'READY' &&
            file.status !== 'READY_NOT_ANALYZED' &&
            file.status !== 'READY_PASSWORD_PROTECTED'
        ).length > 0
      ) {
        const handle = setInterval(async () => {
          const filesInAnalysis = this.fileListStore.getAllFiles.filter(
            (file) => file.status === 'ANALYZING'
          )

          for (const file of filesInAnalysis) {
            const printSpecs = await getFilePrintSpecs(
              Cookies.get(UPLOAD_COOKIE),
              file.uuid
            )
            await this.fileListStore.updatePrintSpecs({
              update: printSpecs
            })
          }

          if (
            this.fileListStore.getAllFiles.filter(
              (file) => file.status === 'ANALYZING'
            ).length === 0
          ) {
            clearInterval(handle)
          }
        }, 500)
      }
    }
  }
}
</script>

<template>
  <uploader
    :options="uploaderOptions"
    ref="uploader"
    :auto-start="this.rootStore.loggedIn"
    :accept="accept"
    @file-added="fileAdded"
    @on-files-added="filesAdded"
    @file-progress="fileProgress"
    @file-error="fileError"
    @file-success="fileSuccess"
    @file-removed="fileRemoved"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
  >
    <slot></slot>
  </uploader>
</template>

<style scoped lang="scss"></style>
